import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  Timestamp,
  deleteField
} from 'firebase/firestore';
import { db } from '../firebase/config';

export const DatabaseService = {
  // Poll operations
  createPoll: async (userId, pollData) => {
    try {
      const pollRef = await addDoc(collection(db, 'polls'), {
        ...pollData,
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,            // Always active when created
        status: 'waiting',         // 'waiting', 'voting', 'completed'
        participantIds: [],        // Stores IDs of participants
        votes: []                  // Stores vote IDs
      });
      
      // Update user's pollsCreated array
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        pollsCreated: arrayUnion(pollRef.id)
      });
      
      return { id: pollRef.id };
    } catch (error) {
      console.error("Error creating poll: ", error);
      throw error;
    }
  },
  
  getPoll: async (pollId) => {
    try {
      const pollRef = doc(db, 'polls', pollId);
      const pollSnap = await getDoc(pollRef);
      
      if (pollSnap.exists()) {
        const pollData = pollSnap.data();
        
        // Get participants count if not available directly
        let participantCount = 0;
        if (Array.isArray(pollData.participantIds)) {
          participantCount = pollData.participantIds.length;
        }
        
        // Format dates for display
        let createdAtFormatted = "Invalid Date";
        if (pollData.createdAt) {
          // Handle Firestore timestamps
          if (typeof pollData.createdAt === 'object' && pollData.createdAt.toDate) {
            createdAtFormatted = pollData.createdAt.toDate().toISOString();
          } 
          // Handle ISO strings
          else if (typeof pollData.createdAt === 'string') {
            createdAtFormatted = pollData.createdAt;
          }
        }
        
        return { 
          id: pollSnap.id, 
          ...pollData,
          createdAt: createdAtFormatted,
          participantCount
        };
      } else {
        throw new Error('Poll not found');
      }
    } catch (error) {
      console.error("Error getting poll: ", error);
      throw error;
    }
  },
  
  updatePoll: async (pollId, updateData) => {
    try {
      const pollRef = doc(db, 'polls', pollId);
      await updateDoc(pollRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error("Error updating poll: ", error);
      throw error;
    }
  },
  
  updatePollStatus: async (pollId, status) => {
    try {
      const pollRef = doc(db, 'polls', pollId);
      
      // If status is 'completed', also set isActive to false
      if (status === 'completed') {
        await updateDoc(pollRef, {
          status,
          isActive: false,
          updatedAt: serverTimestamp()
        });
      } else {
        await updateDoc(pollRef, {
          status,
          updatedAt: serverTimestamp()
        });
      }
      
      return { success: true };
    } catch (error) {
      console.error("Error updating poll status: ", error);
      throw error;
    }
  },
  
  deletePoll: async (pollId, userId) => {
    try {
      // Delete the poll document
      await deleteDoc(doc(db, 'polls', pollId));
      
      // Delete related participants
      const participantsQuery = query(
        collection(db, 'participants'),
        where('pollId', '==', pollId)
      );
      
      const participantSnap = await getDocs(participantsQuery);
      const deleteParticipants = participantSnap.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      
      // Delete related votes
      const votesQuery = query(
        collection(db, 'votes'),
        where('pollId', '==', pollId)
      );
      
      const votesSnap = await getDocs(votesQuery);
      const deleteVotes = votesSnap.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      
      // Wait for all deletions to complete
      await Promise.all([...deleteParticipants, ...deleteVotes]);
      
      // Update user's pollsCreated array
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const updatedPolls = userData.pollsCreated.filter(id => id !== pollId);
        
        await updateDoc(userRef, {
          pollsCreated: updatedPolls
        });
      }
      
      return { success: true };
    } catch (error) {
      console.error("Error deleting poll: ", error);
      throw error;
    }
  },
  
  // Participant management
  joinPoll: async (pollId, userInfo) => {
    try {
      console.log('Joining poll:', pollId, 'with user info:', userInfo);
      
      // Get the poll reference
      const pollRef = doc(db, 'polls', pollId);
      const pollSnap = await getDoc(pollRef);
      
      // Check if poll exists and is active
      if (!pollSnap.exists()) {
        throw new Error('Poll not found');
      }
      
      if (!pollSnap.data().isActive) {
        throw new Error('This poll is no longer active');
      }
      
      // Ensure userInfo is properly structured
      const sanitizedUserInfo = {
        pollId,
        userId: userInfo.userId || 'anonymous',
        name: userInfo.name || 'Anonymous Participant',
        joinedAt: serverTimestamp(),
        hasVoted: false
      };
      
      console.log('Creating participant with data:', sanitizedUserInfo);
      
      // Create participant document
      const participantRef = await addDoc(collection(db, 'participants'), sanitizedUserInfo);
      
      console.log('Created participant document with ID:', participantRef.id);
      
      // Update poll participants list
      await updateDoc(pollRef, {
        participantIds: arrayUnion(participantRef.id),
        updatedAt: serverTimestamp()
      });
      
      console.log('Updated poll with participant ID');
      
      return { success: true, participantId: participantRef.id };
    } catch (error) {
      console.error("Error joining poll: ", error);
      throw error;
    }
  },
  
  // Voting operations
  submitVote: async (pollId, userId, rankingsData, participantId = null) => {
    try {
      // Get the poll
      const pollRef = doc(db, 'polls', pollId);
      const pollSnap = await getDoc(pollRef);
      
      if (!pollSnap.exists()) {
        throw new Error('Poll not found');
      }
      
      // Check if poll is in voting status
      const pollData = pollSnap.data();
      if (pollData.status !== 'voting') {
        throw new Error('Voting is not currently open for this poll');
      }
      
      // Create vote document
      const voteRef = await addDoc(collection(db, 'votes'), {
        pollId,
        userId: userId || 'anonymous',
        rankings: rankingsData,
        participantId, // Store the participantId with the vote
        timestamp: serverTimestamp()
      });
      
      // Update poll with vote ID
      await updateDoc(pollRef, {
        votes: arrayUnion(voteRef.id),
        updatedAt: serverTimestamp()
      });
      
      // If user is authenticated, update their pollsParticipated array
      if (userId) {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          pollsParticipated: arrayUnion(pollId)
        });
      }
      
      // Update participant's hasVoted status if we have participantId
      if (participantId) {
        const participantRef = doc(db, 'participants', participantId);
        const participantSnap = await getDoc(participantRef);
        
        if (participantSnap.exists()) {
          await updateDoc(participantRef, {
            hasVoted: true,
            voteId: voteRef.id // Store the vote ID for reference
          });
        }
      } else if (userId) {
        // If no participantId but we have userId, try to find and update participant
        const participantsQuery = query(
          collection(db, 'participants'),
          where('pollId', '==', pollId),
          where('userId', '==', userId)
        );
        
        const participantSnap = await getDocs(participantsQuery);
        if (!participantSnap.empty) {
          const participantDoc = participantSnap.docs[0];
          await updateDoc(participantDoc.ref, {
            hasVoted: true,
            voteId: voteRef.id
          });
        }
      }
      
      return { success: true, voteId: voteRef.id };
    } catch (error) {
      console.error("Error submitting vote: ", error);
      throw error;
    }
  },
  
  // Real-time subscriptions
  subscribeToPollParticipants: (pollId, onParticipantsUpdate, onStatusUpdate, onError) => {
    try {
      // Subscribe to the poll document for status updates
      const pollRef = doc(db, 'polls', pollId);
      const pollUnsubscribe = onSnapshot(pollRef, (snapshot) => {
        if (snapshot.exists()) {
          const pollData = snapshot.data();
          onStatusUpdate(pollData.status);
        }
      }, (error) => {
        console.error("Error in poll status listener: ", error);
        onError(error);
      });
      
      // Subscribe to participants collection filtered by this poll
      const participantsQuery = query(
        collection(db, 'participants'),
        where('pollId', '==', pollId),
        orderBy('joinedAt', 'asc')
      );
      
      const participantsUnsubscribe = onSnapshot(participantsQuery, (snapshot) => {
        const participants = [];
        snapshot.forEach((doc) => {
          participants.push({
            id: doc.id,
            ...doc.data(),
            // Convert Firestore Timestamp to JS Date for easier display
            joinedAt: doc.data().joinedAt instanceof Timestamp ? 
              doc.data().joinedAt.toDate().toISOString() : 
              new Date().toISOString()
          });
        });
        
        onParticipantsUpdate(participants);
      }, (error) => {
        console.error("Error in participants listener: ", error);
        onError(error);
      });
      
      // Return function to unsubscribe from both listeners
      return () => {
        pollUnsubscribe();
        participantsUnsubscribe();
      };
    } catch (error) {
      console.error("Error setting up subscriptions: ", error);
      onError(error);
      // Return empty function as fallback
      return () => {};
    }
  },
  
  // User operations
  getUserPolls: async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        throw new Error('User not found');
      }
      
      const userData = userSnap.data();
      const pollIds = userData.pollsCreated || [];
      
      // Fetch all poll data
      const polls = [];
      for (const pollId of pollIds) {
        try {
          const pollRef = doc(db, 'polls', pollId);
          const pollSnap = await getDoc(pollRef);
          
          if (pollSnap.exists()) {
            const pollData = pollSnap.data();
            
            // Get participants count
            let participantCount = 0;
            if (Array.isArray(pollData.participantIds)) {
              participantCount = pollData.participantIds.length;
            }
            
            // Format created date
            let createdAtFormatted = null;
            if (pollData.createdAt) {
              // Handle Firestore timestamps
              if (typeof pollData.createdAt === 'object' && pollData.createdAt.toDate) {
                createdAtFormatted = pollData.createdAt.toDate().toISOString();
              } 
              // Handle ISO strings
              else if (typeof pollData.createdAt === 'string') {
                createdAtFormatted = pollData.createdAt;
              }
            }
            
            polls.push({
              id: pollId,
              ...pollData,
              createdAt: createdAtFormatted,
              participantCount
            });
          }
        } catch (err) {
          console.error(`Error fetching poll ${pollId}:`, err);
        }
      }
      
      return polls;
    } catch (error) {
      console.error("Error getting user polls: ", error);
      throw error;
    }
  },
  
  getUserParticipatedPolls: async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        throw new Error('User not found');
      }
      
      const userData = userSnap.data();
      const pollIds = userData.pollsParticipated || [];
      
      // Fetch all poll data
      const polls = [];
      for (const pollId of pollIds) {
        try {
          const pollRef = doc(db, 'polls', pollId);
          const pollSnap = await getDoc(pollRef);
          
          if (pollSnap.exists()) {
            const pollData = pollSnap.data();
            
            // Get participants count
            let participantCount = 0;
            if (Array.isArray(pollData.participantIds)) {
              participantCount = pollData.participantIds.length;
            }
            
            // Format created date
            let createdAtFormatted = null;
            if (pollData.createdAt) {
              // Handle Firestore timestamps
              if (typeof pollData.createdAt === 'object' && pollData.createdAt.toDate) {
                createdAtFormatted = pollData.createdAt.toDate().toISOString();
              } 
              // Handle ISO strings
              else if (typeof pollData.createdAt === 'string') {
                createdAtFormatted = pollData.createdAt;
              }
            }
            
            polls.push({
              id: pollId,
              ...pollData,
              createdAt: createdAtFormatted,
              participantCount
            });
          }
        } catch (err) {
          console.error(`Error fetching poll ${pollId}:`, err);
        }
      }
      
      return polls;
    } catch (error) {
      console.error("Error getting participated polls: ", error);
      throw error;
    }
  },
  
  // Search functionality
  searchPolls: async (searchTerm, limitCount = 10) => {
    try {
      // This is a simple implementation. For better search, consider using Firebase Extensions like Algolia
      const pollsRef = collection(db, 'polls');
      const q = query(
        pollsRef,
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const results = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Search in title, description, and category
        if (
          data.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (data.description && data.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (data.category && data.category.toLowerCase().includes(searchTerm.toLowerCase()))
        ) {
          results.push({ id: doc.id, ...data });
        }
      });
      
      return results.slice(0, limitCount);
    } catch (error) {
      console.error("Error searching polls: ", error);
      throw error;
    }
  },
  
  // Get poll results
  getPollResults: async (pollId) => {
    try {
      // Get the poll
      const pollRef = doc(db, 'polls', pollId);
      const pollSnap = await getDoc(pollRef);
      
      if (!pollSnap.exists()) {
        throw new Error('Poll not found');
      }
      
      const pollData = pollSnap.data();
      const voteIds = pollData.votes || [];
      
      // Collect all votes
      const votes = [];
      for (const voteId of voteIds) {
        const voteRef = doc(db, 'votes', voteId);
        const voteSnap = await getDoc(voteRef);
        
        if (voteSnap.exists()) {
          votes.push(voteSnap.data());
        }
      }
      
      return {
        pollData,
        votes,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error("Error getting poll results: ", error);
      throw error;
    }
  },

  // Get participant by ID
  getParticipant: async (pollId, participantId) => {
    try {
      if (!participantId) {
        return null;
      }
      
      const participantRef = doc(db, 'participants', participantId);
      const participantSnap = await getDoc(participantRef);
      
      if (participantSnap.exists()) {
        return { id: participantSnap.id, ...participantSnap.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error getting participant: ", error);
      throw error;
    }
  }
};